import React, { useState, useEffect } from 'react';
//
import { Organization, OrganizationUpdate, ForgeClient } from '../../types';
import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { Label } from '@/components/Common/label';
import { ConfirmationModal } from '@/components/Common/ConfirmationModal';
import configurationService from '@/services/configurationService';
import * as forgeService from '@/services/forgeService';
import { useIsSuperAdmin } from '@/stores/authStore';
import { useForgeApiEnabled } from '@/stores/forgeApiStore';
import { useTranslation } from 'react-i18next';

interface EditOrganizationModalProps {
  organization: Organization;
  onClose: () => void;
  onSubmit: (data: OrganizationUpdate) => void;
}

interface SectorTemplate {
  id: string;
  name: string;
  description: string;
}

const EditOrganizationModal: React.FC<EditOrganizationModalProps> = ({
  organization,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation();
  const isSuperAdmin = useIsSuperAdmin();
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const [formData, setFormData] = useState<OrganizationUpdate>({
    name: organization.name,
    description: organization.description || '',
    domain: organization.domain || '',
    sector_template_id: organization.sector_template_id || '',
    is_active: organization.is_active,
    forge_client_id: organization.forge_client_id || null,
  });
  const [sectorTemplates, setSectorTemplates] = useState<SectorTemplate[]>([]);
  const [forgeClients, setForgeClients] = useState<ForgeClient[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingForgeClients, setLoadingForgeClients] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSectorChangeConfirm, setShowSectorChangeConfirm] = useState(false);

  useEffect(() => {
    setFormData({
      name: organization.name,
      description: organization.description || '',
      domain: organization.domain || '',
      sector_template_id: organization.sector_template_id || '',
      is_active: organization.is_active,
      forge_client_id: organization.forge_client_id || null,
    });
  }, [organization]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const response = await configurationService.getTemplates();
        setSectorTemplates(response.templates ?? []);
      } catch (error) {
        console.error('Error loading sector templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    void loadTemplates();
  }, []);

  useEffect(() => {
    if (!isSuperAdmin || !forgeApiEnabled) return;
    const loadForgeClients = async () => {
      try {
        setLoadingForgeClients(true);
        const clients = await forgeService.listClients();
        setForgeClients(clients);
      } catch (error) {
        console.error('Error loading forge clients:', error);
      } finally {
        setLoadingForgeClients(false);
      }
    };
    void loadForgeClients();
  }, [isSuperAdmin, forgeApiEnabled]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = t("organizations.modals.edit.errors.nameRequired", "Le nom de l'organisation est requis");
    }

    if (formData.domain && !/^(https?:\/\/)?([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})(\/.*)?$/.test(formData.domain)) {
      newErrors.domain = t('organizations.modals.edit.errors.domainInvalid', 'Format de domaine invalide');
    }

    if (!formData.sector_template_id) {
      newErrors.sectorTemplate = t(
        'organizations.modals.edit.errors.sectorRequired',
        "Le secteur de l'organisation est requis"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const sectorChanged = formData.sector_template_id !== (organization.sector_template_id || '');
    if (sectorChanged) {
      setShowSectorChangeConfirm(true);
      return;
    }

    onSubmit(formData);
  };

  const handleConfirmSectorChange = () => {
    setShowSectorChangeConfirm(false);
    onSubmit(formData);
  };

  const handleChange = (field: keyof OrganizationUpdate, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal open title={t("organizations.modals.edit.title", "Modifier l'Organisation")} onClose={onClose}>
      <>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">
              {t("organizations.modals.edit.fields.nameLabel", "Nom de l'organisation *")}
            </Label>
            <Input
              id="name"
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('organizations.modals.edit.fields.namePlaceholder', 'Ex: ACME Corporation')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-destructive">{errors.name}</p>
            )}
          </div>
          <div>
            <Label htmlFor="description">
              {t('organizations.modals.edit.fields.descriptionLabel', 'Description')}
            </Label>
            <textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-border bg-background p-2 text-sm"
              placeholder={t(
                'organizations.modals.edit.fields.descriptionPlaceholder',
                "Description de l'organisation...",
              )}
            />
          </div>
          <div>
            <Label htmlFor="domain">
              {t('organizations.modals.edit.fields.domainLabel', 'Domaine')}
            </Label>
            <Input
              id="domain"
              value={formData.domain || ''}
              onChange={(e) => handleChange('domain', e.target.value)}
              placeholder={t('organizations.modals.edit.fields.domainPlaceholder', 'Ex: acme.com')}
            />
            {errors.domain && (
              <p className="mt-1 text-sm text-destructive">{errors.domain}</p>
            )}
          </div>
          <div>
            <Label htmlFor="sector-template">
              {t('organizations.modals.edit.fields.sectorLabel', 'Secteur *')}
            </Label>
            <select
              id="sector-template"
              value={formData.sector_template_id || ''}
              onChange={(e) => {
                handleChange('sector_template_id', e.target.value);
                if (errors.sectorTemplate) {
                  setErrors(prev => ({ ...prev, sectorTemplate: '' }));
                }
              }}
              className="mt-1 block h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={loadingTemplates}
            >
              <option value="">
                {loadingTemplates
                  ? t('organizations.modals.edit.fields.sectorLoading', 'Chargement des secteurs...')
                  : t('organizations.modals.edit.fields.sectorPlaceholder', 'Sélectionnez un secteur')}
              </option>
              {sectorTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
            {errors.sectorTemplate && (
              <p className="mt-1 text-sm text-destructive">{errors.sectorTemplate}</p>
            )}
          </div>
          {isSuperAdmin && forgeApiEnabled && (
            <div>
              <Label htmlFor="forge-client">
                {t('organizations.modals.edit.fields.forgeClientLabel', 'Client Forge')}
              </Label>
              <select
                id="forge-client"
                value={formData.forge_client_id ?? ''}
                onChange={(e) => setFormData(prev => ({ ...prev, forge_client_id: e.target.value || null }))}
                className="mt-1 block h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                disabled={loadingForgeClients}
              >
                <option value="">
                  {loadingForgeClients
                    ? t('organizations.modals.edit.fields.forgeClientLoading', 'Chargement...')
                    : t('organizations.modals.edit.fields.forgeClientNone', 'Aucun client Forge')}
                </option>
                {forgeClients.map((client) => (
                  <option key={client.client_id} value={client.client_id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active || false}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="is_active">{t('organizations.activeOrganization')}</Label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('global.cancel')}
            </Button>
            <Button type="submit">
              {t('global.save')}
            </Button>
          </div>
        </form>

        <ConfirmationModal
          open={showSectorChangeConfirm}
          title={t('organizations.modals.edit.alerts.sectorChangeTitle', 'Confirmer le changement de secteur')}
          message={t(
            'organizations.modals.edit.alerts.sectorChangeResetsKnowledge',
            "Changer le secteur va écraser la configuration de connaissance existante avec les fichiers par défaut de ce secteur. Continuer ?"
          )}
          confirmText={t('global.confirm', 'Confirmer')}
          cancelText={t('global.cancel')}
          variant="warning"
          onConfirm={handleConfirmSectorChange}
          onCancel={() => setShowSectorChangeConfirm(false)}
        />
      </>
    </Modal>
  );
};

export default EditOrganizationModal;
